import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { Navigate, useSearchParams, Link, useNavigate } from 'react-router-dom'
import { setUser as setSentryUser } from '@sentry/react'
import { ArrowLongRightIcon, ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useState, useEffect } from 'react'

import { useCurrentUser } from '@contexts/currentUser'
import { useAnalytics } from '@contexts/analytics'
import { client } from '@helpers/api'
import { Label } from '@components/ui/label'
import { Button } from '@components/ui/button'
import Card from '@components/Card'
import { Input } from '@components/ui/input'
import FullLayout from '@pages/FullLayout'

const SignIn = () => {
  const [searchParams] = useSearchParams()
  const { identify } = useAnalytics()
  const { register, handleSubmit, setError, formState: { errors }, getValues, reset } = useForm({ mode: 'onTouched' })
  const { user: { signedIn, super_admin: superAdmin }, setUser, currentMembership } = useCurrentUser()
  const [showOTP, setShowOTP] = useState(false)
  const [credentials, setCredentials] = useState({ email: '', password: '' })

  const navigate = useNavigate()

  // Focus on OTP input when it appears
  useEffect(() => {
    if (showOTP) {
      // Use setTimeout to ensure the OTP field has been rendered
      setTimeout(() => {
        const otpInput = document.getElementById('otp')
        if (otpInput) otpInput.focus()
      }, 100)
    }
  }, [showOTP])

  const { mutate: signIn, isLoading } = useMutation({
    mutationFn: async variables => await client.post('/users/sign_in', variables),
    onSuccess: response => {
      if (response.data.mfa_required) {
        const values = getValues();
        setCredentials({ email: values.email, password: values.password })
        setShowOTP(true)
      } else {
        setUser(response.data)
        setSentryUser({ id: response.data.id, email: response.data.email })
        identify(response.data.id)
      }
    },
    onError: error => {
      const message = error?.response?.data?.error

      if (message === 'Invalid Email or password.') {
        setError('email', { message: 'Invalid email or password' })
        if (showOTP) {
          setError('otp', { message: 'Invalid OTP' })
        }
      }

      if (message === 'You have to confirm your email address before continuing.') {
        const email = encodeURIComponent(getValues('email'))
        navigate(`/email_confirmation?email=${email}`)
      }
    }
  })

  const handleBack = () => {
    setShowOTP(false)
  }

  const submit = data => {
    if (showOTP) {
      signIn({
        user: {
          email: credentials.email,
          password: credentials.password,
          otp_attempt: data.otp
        }
      })
    } else {
      signIn({ user: data })
    }
  }

  const redirectPath = searchParams.get('redirect_to') || ''

  if (signedIn) {
    if (redirectPath) {
      return <Navigate to={redirectPath} />
    } else if (currentMembership?.role === 'student') {
      return <Navigate to='/students' />
    } else if (superAdmin) {
      return <Navigate to='/admin' />
    }

    return <Navigate to='/educators' />
  }

  const cookies = document.cookie.split('; ')
  const csrfToken = cookies?.find(cookie => cookie.startsWith('CSRF-TOKEN='))?.split('=')[1]

  // OTP Form Content
  const OTPForm = () => (
    <Card className='p-5 sm:w-[400px] self-center'>
      <div className="flex items-center mb-4">
        <button
          type="button"
          onClick={handleBack}
          className="mr-2 p-1 rounded-full hover:bg-gray-100"
          aria-label="Back to sign in"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className='text-2xl font-bold'>Enter Authentication Code</h1>
      </div>

      <p className='text-gray-600 mb-4'>
        Please enter the one-time password from your authenticator app.
      </p>

      <form className='flex flex-col space-y-4' onSubmit={handleSubmit(submit)}>
        <div>
          <Label htmlFor='otp'>OTP Code</Label>
          <Input
            id='otp'
            required
            {...register('otp', { required: 'OTP is required' })}
          />
          <If condition={errors.otp}>
            <p className='text-sm mt-1 text-red-500' role='alert'>{errors.otp.message}</p>
          </If>
        </div>

        <If condition={errors.general}>
          <p className='text-sm mt-1 text-red-500' role='alert'>{errors.general.message}</p>
        </If>

        <Button
          type='submit'
          disabled={isLoading}
          loading={isLoading}
        >
          Verify
        </Button>
      </form>
    </Card>
  );

  // Regular Sign In Form Content
  const SignInForm = () => (
    <>
      <Card className='p-5 sm:w-[400px] self-center'>
        <h1 className='text-2xl font-bold'>Sign in</h1>

        <form className="mt-3" method='post' action={`${import.meta.env.VITE_API_URL}/users/auth/google_app?intent=sign_in&redirect_to=${redirectPath}`}>
          <input type='hidden' name='authenticity_token' value={csrfToken} />


          <Button
            type='submit'
            variant='outline'
            className='text-center w-full'>
            <img
              className='w-5 mr-5'
              src='google-logo.svg'
            />

            <span>Sign in with Google</span>

            <ArrowLongRightIcon className='ml-auto w-6' />
          </Button>
        </form>

        <form className="mt-3" method='post' action={`${import.meta.env.VITE_API_URL}/users/auth/microsoft_app?intent=sign_in&redirect_to=${redirectPath}`}>
          <input type='hidden' name='authenticity_token' value={csrfToken} />

          <Button
            type='submit'
            variant='outline'
            className='text-center w-full'
          >
            <img
              className='w-5 mr-5'
              src='microsoft-logo.svg'
            />

            <span>Sign in with Microsoft</span>

            <ArrowLongRightIcon className='ml-auto w-6' />
          </Button>
        </form>

        <div className='flex items-center my-3'>
          <div className='border-b w-full' />
          <span className='p-3'>OR</span>
          <div className='border-b w-full' />
        </div>

        <form className='flex flex-col space-y-4' onSubmit={handleSubmit(submit)}>
          <div className='space-y-1'>
            <Label htmlFor='email'>Email</Label>
            <Input
              type='email'
              id='email'
              required
              {...register('email', { required: 'Email is required' })}
            />
            <If condition={errors.email}>
              <p className='text-sm mt-1 text-destructive' role='alert'>{errors.email.message}</p>
            </If>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='password'>Password</Label>
            <Input
              type='password'
              id='password'
              required
              {...register('password', { required: 'Password is required' })}
            />
            <If condition={errors.password}>
              <p className='text-sm mt-1 text-destructive' role='alert'>{errors.password.message}</p>
            </If>
          </div>

          <If condition={errors.general}>
            <p className='text-sm mt-1 text-destructive' role='alert'>{errors.general.message}</p>
          </If>

          <Button
            type='submit'
            disabled={isLoading}
            loading={isLoading}
          >
            Sign in
          </Button>
        </form>

        <Link to='/request_password_reset' className='mt-3 block underline text-sm'>Forgot password?</Link>
      </Card>

      <p className='flex self-center mt-5 text-sm'>
        New to Mindjoy?&nbsp;<Link to='/sign_up' className='underline'>Sign up</Link>
      </p>
    </>
  );

  // Use the new FullLayout component
  return (
    <div className='flex flex-col items-center justify-center min-h-screen'>
      <FullLayout.Logo />
      <If condition={showOTP}>
        <OTPForm />
      </If>
      <If condition={!showOTP}>
        <SignInForm />
      </If>
    </div>
  );
}

export default SignIn
